import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAlgorithmRunner } from './core/hooks/useAlgorithmRunner';
import { useResize } from './core/hooks/useResize';
import { useStatePlayer } from './core/utils/statePlayer';
import { VisualizerCanvas } from './ui/components/canvas/VisualizerCanvas';
import { AlgorithmSidebar } from './ui/components/sidebar/AlgorithmSidebar';
import PlaybackControls from './ui/components/player/PlaybackControls';
import { CodeViewer } from './ui/components/codeblock/CodeViewer';
import { ExecutionLog } from './ui/components/explanation/ExecutionLog';
import { ErrorBoundary } from './ui/components/error/ErrorBoundary';
import { StatsPanel } from './ui/components/stats/StatsPanel';
import { ComplexityChart } from './ui/components/stats/ComplexityChart';
import { Activity, Layers, SlidersHorizontal, Menu, BarChart2, X } from 'lucide-react';
import { type AlgorithmMeta, getAlgorithmById } from './core/registry/algorithmRegistry';
import { getAlgorithmCode } from './core/utils/codeGenerator';
import { buildInputData } from './core/utils/inputDataBuilder';
import { AlgorithmInputEditor } from './ui/components/playground/AlgorithmInputEditor';
import { AccessibilityProvider } from './core/context/AccessibilityContext';
import { AccessibilityToolbar } from './ui/components/accessibility/AccessibilityToolbar';
import { ExportShareToolbar } from './ui/components/share/ExportShareToolbar';
import { parseShareURL, clearShareParams } from './core/utils/exportShare';
import type { ExecutionStep } from './core/types/rust-engine-interfaces';
import { ComparePanel } from './ui/components/compare/ComparePanel';
import { useCompareRunner } from './core/hooks/useCompareRunner';

function App() {
  const {
    currentSteps,
    execute,
    algorithmId,
  } = useAlgorithmRunner();

  const [selectedAlgo, setSelectedAlgo]     = useState<AlgorithmMeta | null>(null);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(560);
  const [isResetting, setIsResetting]       = useState(false);
  const [selectedLanguage, setSelectedLanguage]   = useState('Python');
  const [sidebarCollapsed, setSidebarCollapsed]   = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [analyticsVisible, setAnalyticsVisible]   = useState(() => typeof window !== 'undefined' && window.innerWidth >= 640);
  const [mode, setMode]                           = useState<'algorithm' | 'compare'>('algorithm');
  const [inputEditorOpen, setInputEditorOpen]     = useState(false);
  const [rightPanelTab, setRightPanelTab]         = useState<'code' | 'log' | 'analytics'>('code');

  const canvasContainerRef = useRef<HTMLDivElement>(null);

  // ── Compare mode ──────────────────────────────────────────────────────────
  const compareRunner = useCompareRunner();
  const [compareMode, setCompareMode] = useState(false);
  // Tracks which slot to fill next when user clicks algo in compare mode
  const nextCompareSlotRef = useRef<'A' | 'B'>('A');

  const clearCompareSlots = useCallback(() => {
    compareRunner.clearSlot('A');
    compareRunner.clearSlot('B');
    nextCompareSlotRef.current = 'A';
  }, [compareRunner]);

  const handleToggleCompareMode = useCallback(() => {
    setCompareMode(v => {
      const next = !v;
      if (next) setMode('compare');
      else {
        setMode('algorithm');
        clearCompareSlots();
      }
      return next;
    });
  }, [clearCompareSlots]);

  const handleSelectForCompare = useCallback((algo: AlgorithmMeta) => {
    const slot = nextCompareSlotRef.current;
    compareRunner.setSlotAlgo(slot, algo);
    // Alternate slots: A → B → A → …
    nextCompareSlotRef.current = slot === 'A' ? 'B' : 'A';
  }, [compareRunner]);

  const statePlayer = useStatePlayer(currentSteps);
  const { setSteps: playerSetSteps, reset: playerReset } = statePlayer;

  useEffect(() => {
    if (algorithmId) {
      const algoMeta = getAlgorithmById(algorithmId);
      if (algoMeta) setSelectedAlgo(algoMeta);
    }
  }, [algorithmId]);

  const handleSelectAlgorithm = useCallback(async (algorithm: AlgorithmMeta) => {
    setSelectedAlgo(algorithm);
    playerSetSteps([]);
    setIsResetting(true);
    const inputData = buildInputData(algorithm);
    try {
      await execute(algorithm.id, inputData);
    } catch (error) {
      console.error('[App] Failed to execute algorithm:', error);
      setIsResetting(false);
    }
    // isResetting is cleared by the effect below once hasSteps becomes true
  }, [execute, playerSetSteps]);

  // ── Restore algorithm + step from share URL (runs once on mount) ────────────
  const pendingShareStepRef = useRef<number | null>(null);
  const handleSelectAlgorithmRef = useRef(handleSelectAlgorithm);
  handleSelectAlgorithmRef.current = handleSelectAlgorithm;
  useEffect(() => {
    const shared = parseShareURL();
    if (!shared) return;
    const algoMeta = getAlgorithmById(shared.algorithmId);
    if (!algoMeta) return;
    pendingShareStepRef.current = shared.stepIndex;
    clearShareParams();
    handleSelectAlgorithmRef.current(algoMeta);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once steps load, seek to the shared step index (if any)
  useEffect(() => {
    if (pendingShareStepRef.current === null) return;
    if (statePlayer.steps.length === 0) return;
    statePlayer.seekTo(pendingShareStepRef.current);
    pendingShareStepRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statePlayer.steps.length]);

  const handleEscape = useCallback(() => {
    setInputEditorOpen(false);
    setMobileSidebarOpen(false);
  }, []);

  const handleSelectAlgoAndCloseSidebar = useCallback((algo: AlgorithmMeta) => {
    handleSelectAlgorithm(algo);
    setMobileSidebarOpen(false);
  }, [handleSelectAlgorithm]);

  const handleToggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed(c => !c);
  }, []);

  const handleMobileSidebarClose = useCallback(() => {
    setMobileSidebarOpen(false);
  }, []);

  const handleResetContext = useCallback(() => {
    setSelectedAlgo(null);
    setIsResetting(false);
    statePlayer.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImport = useCallback((steps: ExecutionStep[], importedAlgoId: string) => {
    const algoMeta = getAlgorithmById(importedAlgoId);
    if (algoMeta) setSelectedAlgo(algoMeta);
    playerSetSteps(steps);
  }, [playerSetSteps]);

  const handleCustomInput = useCallback(async (envelope: unknown) => {
    if (!selectedAlgo) return;
    playerSetSteps([]);
    setIsResetting(true);
    try {
      await execute(selectedAlgo.id, envelope);
    } catch (error) {
      console.error('[App] Failed to execute with custom input:', error);
    } finally {
      setIsResetting(false);
    }
  }, [execute, selectedAlgo, playerSetSteps]);

  const { isResizing, handleResizeStart } = useResize({
    onResize: setRightSidebarWidth,
    minWidth: 350,
    maxWidth: 900,
  });

  const generatedCodeContent = useMemo(() => {
    if (!selectedAlgo) return '';
    return getAlgorithmCode(selectedAlgo.id, selectedLanguage);
  }, [selectedAlgo, selectedLanguage]);

  useEffect(() => {
    playerSetSteps(currentSteps ?? []);
  }, [currentSteps, playerSetSteps]);

  useEffect(() => {
    if (selectedAlgo) playerReset();
  }, [selectedAlgo?.id, playerReset]);

  // Clear isResetting once steps are available in the player
  useEffect(() => {
    if (isResetting && statePlayer.steps.length > 0) {
      setIsResetting(false);
      return undefined;
    }
    // Safety: clear after 15s even if no steps arrived (generator/fallback failure)
    if (isResetting) {
      const timer = setTimeout(() => setIsResetting(false), 15_000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isResetting, statePlayer.steps.length]);

  const currentStep = statePlayer.steps[statePlayer.currentStepIndex] ?? null;
  const hasSteps    = statePlayer.steps.length > 0;

  const currentInputSize = useMemo(() => {
    const payload = currentStep?.payload;
    if (!payload) return 1;
    switch (payload.type) {
      case 'array':
        return Math.max(1, payload.items.length);
      case 'graph':
        return Math.max(1, payload.vertices.length);
      case 'matrix':
        return Math.max(1, Math.max(payload.rows, payload.cols));
      case 'tree':
        return Math.max(1, payload.nodes.length);
      case 'string':
        return Math.max(1, payload.value.length);
      case 'generic':
        return Math.max(1, Object.keys(payload.data).length);
      default:
        return 1;
    }
  }, [currentStep]);

  return (
    <div
      className="flex h-screen w-screen text-zinc-100 font-sans relative overflow-hidden select-none"
      style={{ background: 'var(--surface-0)' }}
    >
      {/* ── Mobile sidebar backdrop ── */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 sm:hidden"
          onClick={handleMobileSidebarClose}
          aria-hidden="true"
        />
      )}

      {/* ── LEFT RAIL — Algorithm Selector Sidebar ── */}
      <AlgorithmSidebar
        selectedAlgo={selectedAlgo}
        onSelectAlgorithm={handleSelectAlgoAndCloseSidebar}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebarCollapse}
        isMobileOpen={mobileSidebarOpen}
        onMobileClose={handleMobileSidebarClose}
        compareMode={compareMode}
        compareSlotA={compareRunner.slotA.algo}
        compareSlotB={compareRunner.slotB.algo}
        onSelectForCompare={handleSelectForCompare}
        onToggleCompareMode={handleToggleCompareMode}
      />

      {/* ── CENTER STAGE — Canvas + Playback ── */}
      <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">

        {/* ── Canvas Stage Card ── */}
        <section className="flex-1 p-3 pb-0 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden rounded-2xl glass-card">

            {/* Canvas Header */}
            <div
              className="flex items-center gap-3 px-4 py-2.5 flex-shrink-0 min-w-0 canvas-header"
              style={{
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                background: 'rgba(255,255,255,0.015)',
              }}
            >
              {selectedAlgo ? (
                <>
                  {/* Status dot + name */}
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{
                        background: hasSteps ? '#10b981' : '#3b82f6',
                        boxShadow: hasSteps
                          ? '0 0 7px rgba(16,185,129,0.6)'
                          : '0 0 7px rgba(59,130,246,0.5)',
                      }}
                    />
                    <h2 className="text-[13px] font-semibold tracking-tight text-zinc-100 truncate leading-none min-w-0">
                      {selectedAlgo.name}
                    </h2>
                    {/* Category pill — hidden on mobile */}
                    <span
                      className="hidden sm:inline-flex flex-shrink-0 text-[10px] font-mono px-2 py-0.5 rounded-md leading-none"
                      style={{
                        background: 'rgba(99,102,241,0.10)',
                        border: '1px solid rgba(99,102,241,0.2)',
                        color: '#a5b4fc',
                      }}
                    >
                      {selectedAlgo.category}
                    </span>
                  </div>

                  {hasSteps && (
                    <div
                      className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-lg animate-fade-in flex-shrink-0 ml-2"
                      style={{
                        background: 'rgba(16,185,129,0.08)',
                        border: '1px solid rgba(16,185,129,0.2)',
                      }}
                    >
                      <Activity className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                      <span className="text-[11px] font-mono text-emerald-400 tabular-nums whitespace-nowrap">
                        {statePlayer.steps.length} steps
                      </span>
                    </div>
                  )}
                </>
              ) : (
                /* Idle header */
                <div className="flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5 text-zinc-700 flex-shrink-0" />
                  <h2 className="text-[13px] font-medium text-zinc-600 tracking-tight">
                    Visualization Stage
                  </h2>
                </div>
              )}

              {/* Mobile sidebar open button */}
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="sm:hidden flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-zinc-500 hover:text-zinc-300 transition-colors"
                style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                aria-label="Open sidebar"
              >
                <Menu className="w-4 h-4" />
              </button>

              {/* Analytics toggle — mobile only */}
              <button
                onClick={() => setAnalyticsVisible(v => !v)}
                className="sm:hidden flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center transition-colors"
                style={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: analyticsVisible ? '#60a5fa' : '#52525b',
                  background: analyticsVisible ? 'rgba(59,130,246,0.10)' : 'transparent',
                }}
                aria-label={analyticsVisible ? 'Hide analytics' : 'Show analytics'}
              >
                <BarChart2 className="w-4 h-4" />
              </button>

              {/* Export / Share toolbar — hidden on mobile to save space */}
              {(mode === 'algorithm') && (
                <div className="hidden sm:flex items-center">
                  <ExportShareToolbar
                    steps={statePlayer.steps}
                    currentStepIndex={statePlayer.currentStepIndex}
                    algorithmId={selectedAlgo?.id ?? ''}
                    algorithmName={selectedAlgo?.name ?? ''}
                    canvasContainerRef={canvasContainerRef}
                    onImport={handleImport}
                    hasSteps={hasSteps}
                  />
                </div>
              )}

              {/* Accessibility toggles — hidden on mobile */}
              <div className="hidden sm:flex items-center">
                <AccessibilityToolbar />
              </div>

              {/* Mode switcher */}
              <div className="flex items-center gap-1 flex-shrink-0 ml-2 mode-switcher">
                <button
                  onClick={() => { setMode('algorithm'); setCompareMode(false); clearCompareSlots(); }}
                  className="px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors"
                  style={{
                    background: mode === 'algorithm' ? 'rgba(99,102,241,0.18)' : 'transparent',
                    color: mode === 'algorithm' ? '#c7d2fe' : '#71717a',
                    border: mode === 'algorithm' ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
                  }}
                >
                  Algorithms
                </button>
                <button
                  onClick={handleToggleCompareMode}
                  className="px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors"
                  style={{
                    background: mode === 'compare' ? 'rgba(99,102,241,0.18)' : 'transparent',
                    color: mode === 'compare' ? '#c7d2fe' : '#71717a',
                    border: mode === 'compare' ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
                  }}
                >
                  Compare
                </button>
              </div>
            </div>

            {/* Canvas Viewport */}
            <div
              ref={canvasContainerRef}
              className="flex-1 overflow-hidden relative"
              style={{
                background: 'radial-gradient(ellipse at 50% -15%, rgba(59,130,246,0.055) 0%, transparent 58%)',
              }}
            >
              {mode === 'compare' ? (
                <ComparePanel runner={compareRunner} />
              ) : (
              <ErrorBoundary
                isolate
                title="Canvas Render Error"
                description="A frame rendering error was caught. The visualizer has been protected. Click 'Reset Context' to clear the current algorithm and start fresh."
                onReset={handleResetContext}
                resetKeys={[selectedAlgo?.id]}
              >
                {isResetting ? (
                  /* Loading spinner */
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 animate-fade-in">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full border border-blue-500/15" />
                      <div className="absolute inset-0 w-10 h-10 rounded-full border-2 border-transparent border-t-blue-500 animate-spin" />
                    </div>
                    <p className="text-[11px] text-zinc-600 font-mono tracking-widest uppercase">
                      Preparing…
                    </p>
                  </div>
                ) : selectedAlgo && hasSteps ? (
                  <VisualizerCanvas
                    key={selectedAlgo.id}
                    currentStep={currentStep}
                    algorithmType={selectedAlgo.category}
                    algorithmId={selectedAlgo.id}
                    allSteps={statePlayer.steps}
                    stepIndex={statePlayer.currentStepIndex}
                  />
                ) : selectedAlgo ? (
                  /* Algorithm selected but steps not loaded */
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 animate-fade-in">
                    <div
                      className="w-10 h-10 rounded-full border border-blue-500/20 flex items-center justify-center animate-pulse-slow"
                    >
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500/40" />
                    </div>
                    <p className="text-[11px] text-zinc-600 font-mono tracking-widest uppercase">
                      Preparing {selectedAlgo.name}…
                    </p>
                  </div>
                ) : (
                  /* Empty state */
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 animate-fade-in">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center"
                      style={{
                        background: 'rgba(255,255,255,0.025)',
                        border: '1px solid rgba(255,255,255,0.07)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                      }}
                    >
                      <svg
                        className="w-8 h-8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="rgba(255,255,255,0.2)"
                        strokeWidth="1.1"
                        strokeLinecap="round"
                      >
                        <circle cx="5"  cy="12" r="2.2" />
                        <circle cx="19" cy="5"  r="2.2" />
                        <circle cx="19" cy="19" r="2.2" />
                        <line x1="7.1" y1="11.2" x2="16.9" y2="6.5" />
                        <line x1="7.1" y1="12.8" x2="16.9" y2="17.5" />
                      </svg>
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-[13px] font-medium text-zinc-500 tracking-tight">
                        Select an algorithm
                      </p>
                      <p className="text-[11px] text-zinc-700">
                        Choose from the sidebar to begin visualization
                      </p>
                    </div>
                  </div>
                )}
              </ErrorBoundary>
              )}
            </div>
          </div>
        </section>

        {/* ── Playback Transport Bar ── */}
        {mode === 'algorithm' && (
        <PlaybackControls
          isPlaying={statePlayer.isPlaying}
          currentStepIndex={statePlayer.currentStepIndex}
          totalSteps={statePlayer.steps.length}
          speed={statePlayer.speed}
          onPlay={statePlayer.play}
          onPause={statePlayer.pause}
          onStepForward={statePlayer.stepForward}
          onStepBackward={statePlayer.stepBackward}
          onSpeedChange={statePlayer.setSpeed}
          onSeek={statePlayer.seekTo}
          canStepForward={statePlayer.canStepForward}
          canStepBackward={statePlayer.canStepBackward}
          algorithmName={selectedAlgo?.name}
          onEscape={handleEscape}
        />
        )}
      </main>

      {/* ── Resize Handle — hidden on mobile ── */}
      <div
        onMouseDown={(e) => handleResizeStart(e, rightSidebarWidth)}
        className="hidden sm:block w-px flex-shrink-0 cursor-col-resize group relative z-10"
        style={{ pointerEvents: isResizing ? 'none' : 'auto' }}
      >
        <div
          className="absolute inset-0 transition-colors duration-150"
          style={{
            background: isResizing
              ? 'rgba(59,130,246,0.55)'
              : 'rgba(255,255,255,0.06)',
          }}
        />
        <div className="absolute inset-y-0 -left-1.5 -right-1.5 group-hover:bg-blue-500/[0.08] transition-colors duration-200" />
      </div>

      {/* ── RIGHT RAIL — Code Viewer, Execution Log, Analytics ── */}
      <aside
        className={`right-rail flex-shrink-0 flex flex-col h-full overflow-hidden${analyticsVisible ? '' : ' right-rail-mobile-hidden'}`}
        style={{
          width: rightSidebarWidth,
          background: 'linear-gradient(180deg, #111113 0%, #0e0e10 100%)',
          borderLeft: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Mobile close button for right rail overlay */}
        <button
          onClick={() => setAnalyticsVisible(false)}
          className="sm:hidden flex-shrink-0 w-full flex items-center justify-center gap-2 py-2 text-[11px] font-medium text-zinc-400"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}
          aria-label="Close panel"
        >
          <X className="w-3.5 h-3.5" />
          Close
        </button>
        {/* Input Editor — collapsible panel above the code viewer */}
        {selectedAlgo && mode === 'algorithm' && (
          <div
            className="flex-shrink-0"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            <button
              onClick={() => setInputEditorOpen((o) => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-400 transition-colors"
              style={{ background: 'rgba(255,255,255,0.02)' }}
            >
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-3 h-3" />
                Custom Input
              </div>
              <span className="text-zinc-700">{inputEditorOpen ? '▲' : '▼'}</span>
            </button>
            {inputEditorOpen && (
              <AlgorithmInputEditor
                algorithm={selectedAlgo}
                onInputReady={handleCustomInput}
              />
            )}
          </div>
        )}

        {/* Tabbed bottom panel: Code / Log / Analytics */}
        <div className="flex-1 flex flex-col min-h-[200px] overflow-hidden">
          {/* Tab bar */}
          <div className="flex items-center gap-0.5 flex-shrink-0 px-1 pt-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {([
              { id: 'code' as const, label: 'Code', icon: <Layers className="w-3 h-3" /> },
              { id: 'log' as const, label: 'Steps', icon: <Activity className="w-3 h-3" /> },
              ...(selectedAlgo && hasSteps ? [{ id: 'analytics' as const, label: 'Stats', icon: <BarChart2 className="w-3 h-3" /> }] : []),
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setRightPanelTab(tab.id)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors rounded-t ${
                  rightPanelTab === tab.id
                    ? 'text-zinc-200 bg-white/[0.04]'
                    : 'text-zinc-500 hover:text-zinc-400'
                }`}
                style={rightPanelTab === tab.id ? { borderBottom: '2px solid #6366f1' } : undefined}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {rightPanelTab === 'code' && (
              <div className="h-full flex flex-col overflow-hidden">
                <CodeViewer
                algorithm={selectedAlgo}
                codeContent={generatedCodeContent}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                activeLine={statePlayer.steps[statePlayer.currentStepIndex]?.metadata?.line}
              />
              </div>
            )}
            {rightPanelTab === 'log' && (
              <div className="h-full overflow-y-auto">
                <ExecutionLog
                  steps={statePlayer.steps}
                  currentStepIndex={statePlayer.currentStepIndex}
                />
              </div>
            )}
            {rightPanelTab === 'analytics' && selectedAlgo && hasSteps && (
              <div className="h-full overflow-y-auto p-3 space-y-3 animate-slide-up">
                <div className="panel-card space-y-3">
                  <StatsPanel
                    counters={statePlayer.countersAtCurrentStep}
                    totalSteps={statePlayer.steps.length}
                    currentStepIndex={statePlayer.currentStepIndex}
                    timeComplexity={selectedAlgo.complexity.time}
                    spaceComplexity={selectedAlgo.complexity.space}
                  />
                </div>
                <div className="panel-card">
                  <ComplexityChart
                    timeComplexity={selectedAlgo.complexity.time}
                    currentN={currentInputSize}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function AppWithProviders() {
  return (
    <AccessibilityProvider>
      <App />
    </AccessibilityProvider>
  );
}

export default AppWithProviders;
